import React, { useState } from 'react';
import useStyles from './styles';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
  Checkbox
} from '@material-ui/core';
import Header from '../Header';
import messages from './messages';
import { Formik } from 'formik';
import Alert from '../../Common/Alert';
import { Email } from '@material-ui/icons';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const zipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;

interface RealtorSignUpScreenProps {
  onSignUp: (values: SignUpValues) => void;
  tenantName: string;
  accountCreated?: boolean;
}

interface SignUpValues {
  name: string;
  company: string;
  tenant: string;
  email: string;
  password: string;
  lendersAndTitleCompaniesFeedback: string;
  termsAccepted?: boolean;
  marketAreaZipcode?: string;
}

export default function RealtorSignUpScreen({
  onSignUp,
  tenantName,
  accountCreated
}: RealtorSignUpScreenProps) {
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [error, setError] = useState<{ message?: string }>({});

  const initialValues: SignUpValues = {
    name: '',
    company: '',
    tenant: tenantName,
    email: '',
    password: '',
    lendersAndTitleCompaniesFeedback: '',
    termsAccepted: false,
    marketAreaZipcode: ''
  };

  const classes = useStyles();
  const intl = useIntl();

  const validate = (values: SignUpValues) => {
    const errors: Partial<SignUpValues> = {};
    if (!values.email) {
      errors.email = intl.formatMessage(messages.requiredEmail);
    } else if (!emailRegex.test(values.email)) {
      errors.email = intl.formatMessage(messages.invalidEmail);
    }
    if (!values.password) {
      errors.password = intl.formatMessage(messages.requiredPassword);
    }
    if (!values.name) {
      errors.name = intl.formatMessage(messages.requiredName);
    }
    if (!values.marketAreaZipcode) {
      errors.marketAreaZipcode = intl.formatMessage(
        messages.requiredMarketAreaZipcode
      );
    } else if (!zipCodeRegex.test(values.marketAreaZipcode)) {
      errors.marketAreaZipcode = intl.formatMessage(
        messages.invalidMarketAreaZipcode
      );
    }
    return errors;
  };

  const handleClose = (
    event: React.SyntheticEvent<Element, Event>,
    reason?: string
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSnackBar(false);
  };

  const signUpForm = () => {
    return (
      <>
        <Typography variant="h6">
          <Box mb={2}>
            <FormattedMessage {...messages.text} />
          </Box>
        </Typography>
        <Typography variant="subtitle1">
          <Box mb={3}>
            <FormattedMessage {...messages.benefits} />
          </Box>
        </Typography>
        <Formik
          initialValues={initialValues}
          validate={validate}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await onSignUp(values);
            } catch (err) {
              setError({
                message: err instanceof Error ? err.message : String(err)
              });
              setSubmitting(false);
              setShowSnackBar(true);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => (
            <form onSubmit={handleSubmit} className={classes.form}>
              <div>
                <TextField
                  label={intl.formatMessage(messages.name)}
                  variant="outlined"
                  autoComplete="off"
                  name="name"
                  fullWidth
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  error={!!errors.name && touched.name}
                  helperText={errors.name && touched.name && errors.name}
                />
              </div>
              <div>
                <TextField
                  label={intl.formatMessage(messages.company)}
                  variant="outlined"
                  autoComplete="off"
                  name="company"
                  fullWidth
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.company}
                  error={!!errors.company && touched.company}
                  helperText={
                    errors.company && touched.company && errors.company
                  }
                />
              </div>
              <div>
                <TextField
                  label={intl.formatMessage(messages.email)}
                  variant="outlined"
                  autoComplete="off"
                  name="email"
                  fullWidth
                  disabled={isSubmitting}
                  type="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  error={!!errors.email && touched.email}
                  helperText={errors.email && touched.email && errors.email}
                />
              </div>
              <div>
                <TextField
                  label={intl.formatMessage(messages.password)}
                  variant="outlined"
                  autoComplete="off"
                  name="password"
                  fullWidth
                  disabled={isSubmitting}
                  type="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  error={!!errors.password && touched.password}
                  helperText={
                    errors.password && touched.password && errors.password
                  }
                />
              </div>
              <div>
                <TextField
                  label={intl.formatMessage(messages.marketAreaZipcode)}
                  variant="outlined"
                  autoComplete="off"
                  name="marketAreaZipcode"
                  fullWidth
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.marketAreaZipcode}
                  error={
                    !!errors.marketAreaZipcode && touched.marketAreaZipcode
                  }
                  helperText={
                    errors.marketAreaZipcode &&
                    touched.marketAreaZipcode &&
                    errors.marketAreaZipcode
                  }
                />
              </div>
              <div>
                <Typography variant="subtitle2">
                  <Box mb={3}>
                    <FormattedMessage
                      {...messages.lendersAndTitleCompaniesFeedback}
                    />
                  </Box>
                </Typography>
                <TextField
                  label={intl.formatMessage(messages.letUsKnowWhatYouNeed)}
                  variant="outlined"
                  multiline
                  minRows={5}
                  maxRows={7}
                  inputProps={{ maxLength: 1000 }}
                  name="lendersAndTitleCompaniesFeedback"
                  fullWidth
                  disabled={isSubmitting}
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.lendersAndTitleCompaniesFeedback}
                />
                <Box display="flex" justifyContent="flex-end">
                  <Typography variant="caption" color="textSecondary">
                    {values.lendersAndTitleCompaniesFeedback.length}/1000
                  </Typography>
                </Box>
              </div>
              <div>
                <Checkbox
                  name="termsAccepted"
                  color="primary"
                  checked={values.termsAccepted}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  required
                />
                <Typography variant="body2" display="inline">
                  <FormattedMessage {...messages.termsAndConditions1} />
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage {...messages.termsAndConditions2} />
                  </a>
                </Typography>
              </div>
              {isSubmitting && (
                <Box my={2}>
                  <Typography variant="subtitle1">
                    <FormattedMessage {...messages.creatingAccount} />
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting}
              >
                <FormattedMessage {...messages.title} />
              </Button>
            </form>
          )}
        </Formik>
      </>
    );
  };

  const successView = () => {
    return (
      <Box mt={2}>
        <Typography variant="subtitle1">
          <Box
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gridRowGap={2}
          >
            <Email color="primary" style={{ fontSize: 60 }} />
            <FormattedMessage {...messages.accountCreated} />
          </Box>
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Header />
      <Box position="relative" pb={8}>
        <Container maxWidth="xl" component="div" disableGutters>
          <Grid
            className={classes.root}
            container
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs={8} sm={6} md={4}>
              <Paper className={classes.paper} elevation={0} square>
                <Typography variant="h5">
                  <Box mb={2}>
                    <FormattedMessage {...messages.title} />
                  </Box>
                </Typography>
                {accountCreated ? successView() : signUpForm()}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Snackbar
        open={showSnackBar}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          elevation={6}
          variant="filled"
          onClose={() => setShowSnackBar(false)}
        >
          {error.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
